// Pull down from the top of a section's page to see what's new, as in a phone's own apps. The page itself scrolls
// as it always does (the gesture only listens, it never holds the page), so the iPhone's bounce and the bars that
// wait for it to settle (lib/viewport.ts) are left alone.

// How far the finger travels for each point the spinner comes down: it drags, the way a phone's own pull does.
const DRAG = 0.5
// How far the spinner comes down before letting go refreshes, and the most it comes down.
export const PULL_READY = 64
export const PULL_MOST = 96

// How far the spinner has come down for a finger that has moved dy down the screen.
export function pullDistance(dy: number): number {
  if (dy <= 0) return 0
  const pulled = dy * DRAG
  // Past ready it slows further, so it never runs away from the bar.
  if (pulled <= PULL_READY) return pulled
  return Math.min(PULL_MOST, PULL_READY + (pulled - PULL_READY) * 0.35)
}

// A pull starts only from the very top of the page, with one finger, and not inside a sheet (a sheet is pulled down
// to put it away).
export function canStartPull(
  scrollY: number,
  fingers: number,
  target: Pick<Element, 'closest'> | EventTarget | null,
): boolean {
  if (scrollY > 0 || fingers !== 1) return false
  if (!target || !('closest' in target)) return true
  return !target.closest('[role="dialog"], [role="alertdialog"]')
}

// Which way a finger is going once it has moved far enough to tell: a pull down, or something else (scrolling up the
// page, swiping sideways through photos), which the pull then leaves alone.
export function pullIntent(dx: number, dy: number): 'pull' | 'other' | null {
  if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return null
  return dy > 0 && dy > Math.abs(dx) ? 'pull' : 'other'
}

export type Pull = { onPull: (distance: number) => void; onRelease: (ready: boolean) => void }

// Listens for a pull down from the top of the page. Every listener is passive, so scrolling is never held up.
export function watchPull({ onPull, onRelease }: Pull): () => void {
  let start: { x: number; y: number } | null = null
  let intent: 'pull' | 'other' | null = null
  let distance = 0

  const begin = (e: TouchEvent) => {
    const touch = e.touches[0]
    start =
      touch && canStartPull(window.scrollY, e.touches.length, e.target) ? { x: touch.clientX, y: touch.clientY } : null
    intent = null
    distance = 0
  }
  const move = (e: TouchEvent) => {
    const touch = e.touches[0]
    if (!start || !touch || intent === 'other') return
    if (e.touches.length > 1) return cancel()
    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y
    intent ??= pullIntent(dx, dy)
    if (intent !== 'pull') return
    distance = pullDistance(dy)
    onPull(distance)
  }
  const end = () => {
    if (start && intent === 'pull') onRelease(distance >= PULL_READY)
    start = null
    intent = null
    distance = 0
  }
  const cancel = () => {
    if (start && intent === 'pull') onRelease(false)
    start = null
    intent = 'other'
  }

  window.addEventListener('touchstart', begin, { passive: true })
  window.addEventListener('touchmove', move, { passive: true })
  window.addEventListener('touchend', end, { passive: true })
  window.addEventListener('touchcancel', cancel, { passive: true })
  return () => {
    window.removeEventListener('touchstart', begin)
    window.removeEventListener('touchmove', move)
    window.removeEventListener('touchend', end)
    window.removeEventListener('touchcancel', cancel)
  }
}
